import { Controller } from "@hotwired/stimulus"
import {
  calculateItemGross,
  calculateItemGrossLine,
  calculateItemSubtotal,
  divideAndRound,
  GROSS_PER_RATE,
  grossFromNet,
  netFromGross,
  roundToDecimals,
  UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
} from "../../utils/calculation.util"
import { formatNumber, getCurrencyDecimals, getUserLocale, parseInputNumber } from "../../utils/number-format.util"

// Connects to data-controller="documents--item"
//
// An expense line has a second source of truth beside the net unit price: the hidden
// gross_unit_price (target grossUnitPrice), enabled only while the expense is calculated per gross
// (gross_per_rate). Disabled inputs are not posted, so "enabled" is also what the form sends.
// There, the printed gross of a till receipt is what the line holds and the net follows from it,
// as ExpenseItem does on save (spec §5): typing "Cena s DPH" sets the unit gross, a rate or
// quantity change keeps it, a net edit derives it. A document row has no such field and is
// always calculated from its net.
export default class extends Controller {
  static UNIT_PRICE_MAXIMUM_FRACTION_DIGITS = UNIT_PRICE_MAXIMUM_FRACTION_DIGITS

  static outlets = ["documents--totals-calculator", "documents--item-discount", "documents--item-description"]

  static targets = ["quantity", "unitPrice", "totalPrice", "taxRate", "totalPriceTax", "grossUnitPrice", "destroy"]

  static values = {
    isVatPayer: Boolean,
    defaultTaxRate: Number,
    // Initial currency (set from server). Live value is read from the document's
    // #currency select when available, so changing the currency re-formats line
    // totals at the new precision (VND 0 decimals, EUR 2, BHD 3).
    currency: { type: String, default: "EUR" }
  }

  connect() {
    // Get locale from meta tag (source of truth)
    this.locale = getUserLocale()

    // Re-run currency-aware calculations when the document's currency changes,
    // so existing line totals shift from 2-decimal to 0-decimal (VND, JPY, …).
    // Also notify the totals calculator so the document-level footer refreshes.
    this._onDocumentCurrencyChange = () => {
      this.calculateTotal()
      if (this.isVatPayerValue && this.hasTaxRateTarget) {
        this.#calculateTotalTax()
      }
      this.#notifyTotalsCalculator()
    }
    this.documentCurrencySelect = document.querySelector("#currency")
    if (this.documentCurrencySelect) {
      this.documentCurrencySelect.addEventListener("change", this._onDocumentCurrencyChange)
    }

    this.#adoptExpenseVatMode()

    // Skip initial calculation when fields are pre-populated (e.g. imported documents
    // with stored totals from the source system). User edits still recalculate normally.
    if (this.totalPriceTarget.value) return

    this.calculateTotal()

    if (this.isVatPayerValue && this.hasTaxRateTarget) {
      this.#calculateTotalTax()
    }

    // Notify totals calculator after initial calculations
    requestAnimationFrame(() => {
      this.#notifyTotalsCalculator()
    })
  }

  disconnect() {
    if (this.documentCurrencySelect && this._onDocumentCurrencyChange) {
      this.documentCurrencySelect.removeEventListener("change", this._onDocumentCurrencyChange)
    }
  }

  #currentCurrency() {
    return this.documentCurrencySelect?.value || this.currencyValue
  }

  #currencyFractionOptions() {
    const decimals = getCurrencyDecimals(this.#currentCurrency())
    return { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  }

  onQuantityChange() {
    if (this.isGrossMode()) this.#ensureGrossUnitPrice()
    this.calculateTotal()

    // Recalculate discount total when quantity changes
    if (this.hasDocumentsItemDiscountOutlet) {
      this.documentsItemDiscountOutlet.recalculateTotalDiscount()
    }

    if (this.isVatPayerValue) {
      this.#calculateTotalTax()
    }
    this.#notifyTotalsCalculator()
  }

  onUnitPriceChange() {
    if (this.isGrossMode()) this.#deriveGrossFromNet()
    this.calculateTotal()

    // Recalculate discount total when unit price changes (affects percentage-based discounts)
    if (this.hasDocumentsItemDiscountOutlet) {
      this.documentsItemDiscountOutlet.recalculateTotalDiscount()
    }

    if (this.isVatPayerValue) {
      this.#calculateTotalTax()
    }
    this.#notifyTotalsCalculator()
  }

  onTotalChange() {
    this.#calculateUnitPrice()
    if (this.isGrossMode()) this.#deriveGrossFromNet()

    // Recalculate discount total when total changes (unit price is recalculated, affects percentage-based discounts)
    if (this.hasDocumentsItemDiscountOutlet) {
      this.documentsItemDiscountOutlet.recalculateTotalDiscount()
    }

    if (this.isVatPayerValue) {
      this.#calculateTotalTax()
    }
    this.#notifyTotalsCalculator()
  }

  onTaxRateChange() {
    if (this.isGrossMode()) {
      // The printed gross is the line: a new rate moves the net, not the price on the receipt.
      this.#deriveNetFromGross()
      this.calculateTotal()
      if (this.hasDocumentsItemDiscountOutlet) {
        this.documentsItemDiscountOutlet.recalculateTotalDiscount()
      }
    }
    if (this.isVatPayerValue) {
      this.#calculateTotalTax()
    }
    this.#notifyTotalsCalculator()
    this.dispatch("vat-rate-changed", { bubbles: true })
  }

  onTotalPriceTaxChange() {
    if (this.isGrossMode()) {
      this.#calculateUnitPricesFromGrossTotal()
    } else {
      if (this.isVatPayerValue) {
        this.#calculateTotalPriceTax()
      }
      this.#calculateUnitPrice()
    }
    this.calculateTotal()

    // Recalculate discount total when total with VAT changes (unit price is recalculated, affects percentage-based discounts)
    if (this.hasDocumentsItemDiscountOutlet) {
      this.documentsItemDiscountOutlet.recalculateTotalDiscount()
    }

    this.#notifyTotalsCalculator()
  }

  #calculateUnitPrice() {
    const quantity = parseInputNumber(this.quantityTarget, this.locale)
    const totalPrice = parseInputNumber(this.totalPriceTarget, this.locale)

    // Guard against division by zero or invalid quantity
    // Ensure quantity is a finite number > 0 before dividing
    if (!Number.isFinite(quantity) || quantity <= 0) {
      this.unitPriceTarget.value = formatNumber(0, this.locale, {
        maximumFractionDigits: this.constructor.UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
      })
      return
    }

    // Also validate totalPrice before division
    if (!Number.isFinite(totalPrice)) {
      this.unitPriceTarget.value = formatNumber(0, this.locale, {
        maximumFractionDigits: this.constructor.UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
      })
      return
    }

    const unitPrice = totalPrice / quantity

    // Guard against invalid result - only format when result is a valid finite number
    if (!Number.isFinite(unitPrice)) {
      this.unitPriceTarget.value = formatNumber(0, this.locale, {
        maximumFractionDigits: this.constructor.UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
      })
      return
    }

    this.unitPriceTarget.value = formatNumber(unitPrice, this.locale, {
      maximumFractionDigits: this.constructor.UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
    })
  }

  // Public API: Calculate item total (quantity × unitPrice)
  // Note: Discount is NOT subtracted from item total - it's shown separately in discount row
  // The amount is ItemCalculatable#subtotal_before_discount — exact, rounded
  // once, half away from zero — not Intl formatting a float product, which
  // prints 3 × 1.005 (3.0149999999999997) as 3.01 where the server prints 3.02.
  calculateTotal() {
    const quantity = parseInputNumber(this.quantityTarget, this.locale) || 0
    const totalPrice = calculateItemSubtotal(quantity, this.#unitPrice(), getCurrencyDecimals(this.#currentCurrency()))
    this.totalPriceTarget.value = formatNumber(totalPrice, this.locale, this.#currencyFractionOptions())
  }

  // Public API: Get numeric total price (for calculations)
  getTotalPrice() {
    return parseInputNumber(this.totalPriceTarget, this.locale) || 0
  }

  // Public API: Set total price from numeric value (formats according to locale)
  setTotalPrice(value) {
    const numericValue = typeof value === "number" ? value : parseFloat(value) || 0
    this.totalPriceTarget.value = formatNumber(numericValue, this.locale, this.#currencyFractionOptions())
  }

  // Notify totals calculator that this item has changed
  #notifyTotalsCalculator() {
    // Use outlet for direct communication (more reliable than events)
    if (this.hasDocumentsTotalsCalculatorOutlet) {
      this.documentsTotalsCalculatorOutlet.recalculate()
    }
  }

  // Public API: Get item data for totals calculator. grossUnitPrice is the printed gross of a line
  // calculated per gross, undefined for any other (and for a gross line that has none yet: the
  // totals then price it from its net). A gross line hands over its real rate whoever the supplier
  // is, as the server derives its net from it; any other row counts as 0 % for a non-payer.
  getItemData() {
    const quantity = parseInputNumber(this.quantityTarget, this.locale) || 0
    const unitPrice = this.#unitPrice()
    const gross = this.isGrossMode()
    const taxRate = gross || this.isVatPayerValue ? this.#taxRate() : 0
    const grossUnitPrice = gross ? this.#grossUnitPrice() : undefined
    return { quantity, unitPrice, taxRate, grossUnitPrice }
  }

  // Public API: is the line calculated from its printed gross (gross_per_rate)? Its gross field is
  // there and enabled, which is also what the form posts.
  isGrossMode() {
    return this.hasGrossUnitPriceTarget && !this.grossUnitPriceTarget.disabled
  }

  // Public API: the calculation mode of the expense changed (documents--totals-calculator hands it
  // over, after the mode switch). The gross field is posted in gross_per_rate only. Going gross the
  // printed gross stays when the net already is what it implies, as the server judges it
  // (ExpenseItem#net_edited_alone?), else it is derived from the net; the line's gross total follows
  // the mode, and so does its discount row (a percentage only). Nothing here asks the totals to
  // recalculate: the caller does, once for all the rows.
  applyVatMode(mode) {
    if (!this.hasGrossUnitPriceTarget) return

    const gross = mode === GROSS_PER_RATE
    this.grossUnitPriceTarget.disabled = !gross
    if (gross) this.#keepOrDeriveGrossUnitPrice()

    if (this.hasDocumentsItemDiscountOutlet) {
      this.documentsItemDiscountOutlet.syncGrossMode()
    }
    this.calculateTotal()
    if (this.isVatPayerValue) {
      this.#calculateTotalTax()
    }
  }

  // "+ Pridať položku" posts the form and appends the row build_item rendered for the mode posted.
  // A mode change while that request was in flight (the switch, or a new expense following its type
  // or supplier) reached only the rows already on the page (totals-calculator#vatModeValueChanged),
  // so the row adopts the expense's mode as they did, and the totals follow. Done in connect, where
  // the locale and the currency select are known: the outlet callback can run before it. An
  // invoice's row has no gross field and its calculator no mode, so nothing changes there.
  #adoptExpenseVatMode() {
    if (!this.hasGrossUnitPriceTarget || !this.hasDocumentsTotalsCalculatorOutlet) return

    const calculator = this.documentsTotalsCalculatorOutlet
    if (this.isGrossMode() === calculator.isGrossMode()) return

    this.applyVatMode(calculator.vatModeValue)
    calculator.recalculate()
  }

  #unitPrice() {
    return parseInputNumber(this.unitPriceTarget, this.locale) || 0
  }

  // The rate in the field, whoever the supplier is: it is what the server derives the net from.
  #taxRate() {
    return this.hasTaxRateTarget ? parseInputNumber(this.taxRateTarget, this.locale) || 0 : 0
  }

  // The printed gross unit price, a raw decimal (Rails' or this controller's, never localized);
  // undefined while blank.
  #grossUnitPrice() {
    if (!this.hasGrossUnitPriceTarget) return undefined

    const text = this.grossUnitPriceTarget.value.trim()
    const value = text === "" ? Number.NaN : Number(text)
    return Number.isFinite(value) ? value : undefined
  }

  // Posted as a six-decimal raw decimal, whatever the user's locale.
  #writeGrossUnitPrice(value) {
    this.grossUnitPriceTarget.value = value.toFixed(UNIT_PRICE_MAXIMUM_FRACTION_DIGITS)
  }

  // A line that has no gross yet (a row added on a gross expense) takes it from its net.
  #ensureGrossUnitPrice() {
    return this.#grossUnitPrice() ?? this.#deriveGrossFromNet()
  }

  // Writes the gross the net implies and returns it.
  #deriveGrossFromNet() {
    const derived = grossFromNet(this.#unitPrice(), this.#taxRate())
    this.#writeGrossUnitPrice(derived)
    return derived
  }

  #keepOrDeriveGrossUnitPrice() {
    const printed = this.#grossUnitPrice()
    if (printed !== undefined && this.#unitPrice() === netFromGross(printed, this.#taxRate())) return

    this.#deriveGrossFromNet()
  }

  // ExpenseItem#net_from_gross. A rate of −100 % or below has no net for any gross: the unit price is
  // left as it is, for the presence validations of the server to report.
  #deriveNetFromGross() {
    const rate = this.#taxRate()
    if (100 + rate <= 0) return

    this.#writeUnitPrice(netFromGross(this.#ensureGrossUnitPrice(), rate))
  }

  // Typed "Cena s DPH": the unit gross is the typed gross over the quantity and the unit price
  // follows from it, both rounded to six decimals and neither first rounded to cents, so the pair
  // posted is what the server derives. As with a net total, no quantity gives no unit price.
  #calculateUnitPricesFromGrossTotal() {
    const quantity = parseInputNumber(this.quantityTarget, this.locale)
    const total = parseInputNumber(this.totalPriceTaxTarget, this.locale) || 0
    const grossUnitPrice =
      Number.isFinite(quantity) && quantity > 0 ? divideAndRound(total, quantity, UNIT_PRICE_MAXIMUM_FRACTION_DIGITS) : 0
    this.#writeGrossUnitPrice(grossUnitPrice)

    const rate = this.#taxRate()
    if (100 + rate > 0) this.#writeUnitPrice(netFromGross(grossUnitPrice, rate))
  }

  #writeUnitPrice(value) {
    this.unitPriceTarget.value = formatNumber(value, this.locale, {
      maximumFractionDigits: this.constructor.UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
    })
  }

  #calculateTotalTax() {
    if (!this.hasTaxRateTarget || !this.hasTotalPriceTaxTarget) return

    const decimals = getCurrencyDecimals(this.#currentCurrency())
    const quantity = parseInputNumber(this.quantityTarget, this.locale) || 0
    const unitPrice = this.#unitPrice()
    const taxRate = this.#taxRate()

    // ExpenseItem#gross_total_before_discount in gross mode: the printed unit gross times the
    // quantity, not the net with its own VAT on top (which puts 5,00 at 23 % on 5,01). Otherwise
    // ItemCalculatable#total_before_discount: net + round(net × rate / 100).
    const totalPriceTax = this.isGrossMode()
      ? calculateItemGrossLine({ unitPrice, grossUnitPrice: this.#grossUnitPrice(), quantity, taxRate }, decimals)
      : calculateItemGross(calculateItemSubtotal(quantity, unitPrice, decimals), taxRate, decimals)
    this.totalPriceTaxTarget.value = formatNumber(totalPriceTax, this.locale, this.#currencyFractionOptions())
  }

  #calculateTotalPriceTax() {
    if (!this.hasTaxRateTarget || !this.hasTotalPriceTaxTarget) return
    const totalPriceTax = parseInputNumber(this.totalPriceTaxTarget, this.locale)
    const decimals = getCurrencyDecimals(this.#currentCurrency())
    const totalPrice = roundToDecimals(
      totalPriceTax / (1 + parseInputNumber(this.taxRateTarget, this.locale) / 100),
      decimals
    )
    this.totalPriceTarget.value = formatNumber(totalPrice, this.locale, this.#currencyFractionOptions())
  }

  onDiscountItemChange() {
    this.calculateTotal()

    // Recalculate VAT if applicable
    if (this.isVatPayerValue) {
      this.#calculateTotalTax()
    }

    this.#notifyTotalsCalculator()
  }

  // Public API: Show discount row (called from discount button)
  showDiscount(_event) {
    // Use outlet to call discount controller directly
    if (this.hasDocumentsItemDiscountOutlet) {
      this.documentsItemDiscountOutlet.show()
    }
  }

  // Public API: Show description row (called from description button)
  showDescription(_event) {
    // Use outlet to call description controller directly
    if (this.hasDocumentsItemDescriptionOutlet) {
      this.documentsItemDescriptionOutlet.show()
    }
  }

  // Public API for reverse charge controller via outlets
  enableReverseCharge() {
    if (this.hasTaxRateTarget) {
      this.taxRateTarget.readOnly = true

      this.taxRateTarget.value = 0
      this.onTaxRateChange()
    }
  }

  disableReverseCharge() {
    if (this.hasTaxRateTarget) {
      // Restore VAT rate field to editable state
      this.taxRateTarget.readOnly = false

      // Restore default VAT rate if needed
      const defaultRate = this.defaultTaxRateValue
      if (defaultRate && !Number.isNaN(parseFloat(defaultRate))) {
        this.taxRateTarget.value = Math.round(defaultRate)
      } else {
        this.taxRateTarget.value = 0
      }

      this.onTaxRateChange()
    }
  }
}
